import { PlusIcon } from '@shopify/polaris-icons';
import { Page, Modal, TextField } from '../components/forms';
/**
 * MERGE RULES PAGE
 *
 * Configure rules for automatically merging orders from the same customer
 * into a single delivery.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
  Checkbox,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface MergeRule {
  id: number;
  name: string;
  conditions: {
    match_email?: boolean;
    match_phone?: boolean;
    same_delivery_date?: boolean;
    same_zone?: boolean;
    max_days_apart?: number;
  };
  priority: number;
  enabled: boolean;
  created_at: string;
}

interface RulesResponse {
  data: MergeRule[];
}

export default function MergeRules() {
  const { fetchApi, loading, error } = useApi();
  const [rules, setRules] = useState<MergeRule[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<MergeRule | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<MergeRule | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [matchEmail, setMatchEmail] = useState(true);
  const [matchPhone, setMatchPhone] = useState(false);
  const [sameDeliveryDate, setSameDeliveryDate] = useState(false);
  const [sameZone, setSameZone] = useState(false);
  const [maxDaysApart, setMaxDaysApart] = useState('3');
  const [priority, setPriority] = useState('0');
  const [enabled, setEnabled] = useState(true);

  const loadRules = useCallback(async () => {
    const data = await fetchApi<RulesResponse>('/merge-rules');
    if (data?.data) {
      setRules(data.data.map(rule => ({ ...rule, enabled: Boolean(rule.enabled) })));
    }
  }, [fetchApi]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const resetForm = () => {
    setName('');
    setMatchEmail(true);
    setMatchPhone(false);
    setSameDeliveryDate(false);
    setSameZone(false);
    setMaxDaysApart('3');
    setPriority('0');
    setEnabled(true);
    setEditingRule(null);
  };

  const handleOpenModal = (rule?: MergeRule) => {
    if (rule) {
      setEditingRule(rule);
      setName(rule.name);
      setMatchEmail(rule.conditions.match_email ?? true);
      setMatchPhone(rule.conditions.match_phone ?? false);
      setSameDeliveryDate(rule.conditions.same_delivery_date ?? false);
      setSameZone(rule.conditions.same_zone ?? false);
      setMaxDaysApart(String(rule.conditions.max_days_apart ?? 3));
      setPriority(String(rule.priority));
      setEnabled(rule.enabled);
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleSave = async () => {
    const conditions = {
      match_email: matchEmail,
      match_phone: matchPhone,
      same_delivery_date: sameDeliveryDate,
      same_zone: sameZone,
      max_days_apart: parseInt(maxDaysApart, 10),
    };

    const payload = {
      name,
      conditions,
      priority: parseInt(priority, 10),
      enabled,
    };

    const endpoint = editingRule
      ? `/merge-rules/${editingRule.id}`
      : '/merge-rules';
    const method = editingRule ? 'PUT' : 'POST';

    const result = await fetchApi(endpoint, {
      method,
      body: JSON.stringify(payload),
    });

    if (result) {
      handleCloseModal();
      loadRules();
    }
  };

  const handleDelete = async () => {
    if (!ruleToDelete) return;

    const result = await fetchApi(`/merge-rules/${ruleToDelete.id}`, {
      method: 'DELETE',
    });

    if (result) {
      setDeleteConfirmOpen(false);
      setRuleToDelete(null);
      loadRules();
    }
  };

  const handleToggleEnabled = async (rule: MergeRule) => {
    await fetchApi(`/merge-rules/${rule.id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...rule, enabled: !rule.enabled }),
    });
    loadRules();
  };

  if (loading && rules.length === 0) {
    return (
      <Page title="Merge Rules">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading merge rules...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Merge Rules"
      subtitle="Automatically combine orders from the same customer into a single delivery"
      primaryAction={{
        icon: PlusIcon, content: 'Create Rule',
        onAction: () => handleOpenModal(),
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {rules.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                  headings={['Name', 'Conditions', 'Priority', 'Status', 'Actions']}
                  rows={rules.map((rule) => [
                    rule.name,
                    <BlockStack gap="100" key={`cond-${rule.id}`}>
                      {rule.conditions.match_email && <Badge size="small">Match Email</Badge>}
                      {rule.conditions.match_phone && <Badge size="small">Match Phone</Badge>}
                      {rule.conditions.same_delivery_date && <Badge size="small">Same Date</Badge>}
                      {rule.conditions.same_zone && <Badge size="small">Same Zone</Badge>}
                      {rule.conditions.max_days_apart && (
                        <Badge size="small">{`${rule.conditions.max_days_apart} days max`}</Badge>
                      )}
                    </BlockStack>,
                    rule.priority,
                    <Badge
                      key={`status-${rule.id}`}
                      tone={rule.enabled ? 'success' : undefined}
                    >
                      {rule.enabled ? 'Active' : 'Disabled'}
                    </Badge>,
                    <InlineStack gap="200" key={`actions-${rule.id}`}>
                      <Button size="slim" onClick={() => handleOpenModal(rule)}>
                        Edit
                      </Button>
                      <Button
                        size="slim"
                        onClick={() => handleToggleEnabled(rule)}
                      >
                        {rule.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        size="slim"
                        tone="critical"
                        onClick={() => {
                          setRuleToDelete(rule);
                          setDeleteConfirmOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : (
                <EmptyState
                  heading="No merge rules configured"
                  action={{
                    icon: PlusIcon, content: 'Create Rule',
                    onAction: () => handleOpenModal(),
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Create merge rules to automatically combine orders from the same
                    customer into a single delivery, reducing shipping costs.
                  </p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        title={editingRule ? 'Edit Merge Rule' : 'Create Merge Rule'}
        primaryAction={{
          icon: editingRule ? undefined : PlusIcon, content: 'Save',
          onAction: handleSave,
          loading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: handleCloseModal },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Rule Name"
              requiredIndicator value={name}
              onChange={setName}
              autoComplete="off"
              placeholder="e.g., Same customer same day"
            />

            <Text as="p" fontWeight="semibold">Match Conditions</Text>
            <Text as="p" tone="subdued" variant="bodySm">
              Orders must match ALL selected conditions to be merged.
            </Text>

            <Checkbox
              label="Match by email address"
              checked={matchEmail}
              onChange={setMatchEmail}
            />

            <Checkbox
              label="Match by phone number"
              checked={matchPhone}
              onChange={setMatchPhone}
            />

            <Checkbox
              label="Same delivery date"
              checked={sameDeliveryDate}
              onChange={setSameDeliveryDate}
            />

            <Checkbox
              label="Same delivery zone"
              checked={sameZone}
              onChange={setSameZone}
            />

            <TextField
              label="Maximum days apart"
              type="number"
              value={maxDaysApart}
              onChange={setMaxDaysApart}
              autoComplete="off"
              helpText="Orders placed more than this many days apart won't be merged"
            />

            <TextField
              label="Priority"
              type="number"
              value={priority}
              onChange={setPriority}
              autoComplete="off"
              helpText="Higher priority rules are evaluated first"
            />

            <Checkbox
              label="Rule is active"
              checked={enabled}
              onChange={setEnabled}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete Merge Rule"
        primaryAction={{
          content: 'Delete',
          onAction: handleDelete,
          destructive: true,
          loading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setDeleteConfirmOpen(false) },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            Are you sure you want to delete the rule "{ruleToDelete?.name}"?
            This action cannot be undone.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
